import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Typography,
  Box,
  Stack,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  Divider,
  Chip,
  Alert,
} from "@mui/material";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import GroupRemoveIcon from "@mui/icons-material/GroupRemove";
import { useUpdate, useNotify, useRefresh, useRecordContext, useGetList } from "react-admin";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import {
  ResourceName,
  CLAIMANT_RELATIONSHIP_CHOICES,
  type Claimant,
  type ClaimantRelationship,
  type FamilyMember,
} from "@/types";
import { useFamilyUnit, type FamilyUnitData } from "../hooks/useFamilyUnit";
import {
  claimantHasStoplossClaims,
  calculateFamilyMemberCount,
  isAtFamilyCapacity,
} from "../helpers/familyEligibility";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { alpha } from "@mui/material/styles";

interface ManageFamilyDialogProps {
  open: boolean;
  onClose: () => void;
  familyData: FamilyUnitData | null;
  subscriberRefId?: string;
  onMutationSuccess: () => void;
}

/** Row for an existing family member inside the dialog. */
const MemberManageRow = ({
  member,
  isSubscriber,
  onConfirm,
  onRemove,
  onRelationshipChange,
  disableRemove = false,
}: {
  member: FamilyMember;
  isSubscriber: boolean;
  onConfirm: (id: string) => void;
  onRemove: (id: string) => void;
  onRelationshipChange: (id: string, relationship: ClaimantRelationship) => void;
  disableRemove?: boolean;
}) => {
  const { isDarkMode } = useAppTheme();

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        py: 1,
        px: 1.5,
        borderRadius: 1,
        bgcolor: isDarkMode ? alpha("#fff", 0.03) : alpha("#000", 0.02),
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={500} noWrap>
          {member.name}
        </Typography>
      </Box>

      {member.isPendingLink && (
        <Chip label="Pending" size="small" color="warning" variant="outlined" sx={{ height: 22, fontSize: 11 }} />
      )}

      {isSubscriber ? (
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
          Subscriber
        </Typography>
      ) : (
        <Select
          size="small"
          value={member.relationship || "other"}
          onChange={(e) => onRelationshipChange(member.id, e.target.value as ClaimantRelationship)}
          sx={{ minWidth: 120, height: 32, fontSize: "0.8125rem" }}
        >
          {CLAIMANT_RELATIONSHIP_CHOICES.filter((c) => c.id !== "self").map((choice) => (
            <MenuItem key={choice.id} value={choice.id} sx={{ fontSize: "0.8125rem" }}>
              {choice.name}
            </MenuItem>
          ))}
        </Select>
      )}

      <Box sx={{ display: "flex", gap: 0.5 }}>
        {member.isPendingLink && (
          <Tooltip title="Confirm family link" arrow>
            <IconButton size="small" color="success" onClick={() => onConfirm(member.id)}>
              <CheckCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!isSubscriber && (
          <Tooltip title={disableRemove ? "Cannot remove — family has stoploss claims" : "Remove from family"} arrow>
            <span>
              <IconButton size="small" color="error" onClick={() => onRemove(member.id)} disabled={disableRemove}>
                <PersonRemoveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Stack>
  );
};

export const ManageFamilyDialog = ({
  open,
  onClose,
  familyData: parentFamilyData,
  subscriberRefId,
  onMutationSuccess,
}: ManageFamilyDialogProps) => {
  const claimant = useRecordContext<Claimant>();
  const [update] = useUpdate();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();

  // Capture the subscriber ref ID when the dialog opens so it remains stable even if the
  // current claimant is removed from the family (which sets their subscriber_ref_id to null).
  const [stableSubscriberRefId, setStableSubscriberRefId] = useState<string | undefined>();
  useEffect(() => {
    if (open && subscriberRefId) {
      setStableSubscriberRefId(subscriberRefId);
    }
    if (!open) {
      setStableSubscriberRefId(undefined);
    }
  }, [open, subscriberRefId]);

  const [selectedClaimant, setSelectedClaimant] = useState<Claimant | null>(null);
  const [selectedSubscriber, setSelectedSubscriber] = useState<Claimant | null>(null);
  const [createdSubscriberId, setCreatedSubscriberId] = useState<string | null>(null);
  const [createdSubscriberName, setCreatedSubscriberName] = useState<string | null>(null);
  const [createdSubscriberPolicyId, setCreatedSubscriberPolicyId] = useState<string | null>(null);
  const [addRelationship, setAddRelationship] = useState<ClaimantRelationship>("dependent");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResolveConfirm, setShowResolveConfirm] = useState(false);
  const [showDissolveConfirm, setShowDissolveConfirm] = useState(false);
  const [localMembers, setLocalMembers] = useState<FamilyMember[]>([]);

  // Use our own family unit hook with the stable ref so the dialog doesn't lose data
  // when the parent's claimant record refreshes with subscriber_ref_id = null.
  // In create mode, use createdSubscriberId after the subscriber is set.
  const ownFamilyUnit = useFamilyUnit(stableSubscriberRefId ?? createdSubscriberId ?? undefined);
  const familyData = ownFamilyUnit.data ?? parentFamilyData;

  const isCreateMode = !familyData;

  // In manage mode, scope to the current claimant's client.
  // In create mode (from list), fetch all claimants — no client scoping needed.
  const clientId = claimant?.client_id_ref;
  const hasClientScope = !!clientId;

  // Fetch claimants for autocomplete — either scoped to client or all
  const { data: allClaimants = [], isLoading: claimantsLoading } = useGetList<Claimant>(
    ResourceName.CLAIMANTS,
    {
      filter: hasClientScope ? { client_id_ref: clientId } : {},
      pagination: { page: 1, perPage: 500 },
      sort: { field: "claimant_name", order: "ASC" },
    },
    { enabled: open }
  );

  // Check if any family member has stoploss claims — locks add/remove if true. Read
  // from the family payload's own live counts, not the claimant autocomplete list,
  // which is capped at 500 rows and can miss members on a large client.
  const hasStoplossClaims = !isCreateMode && !!familyData?.members.some((m) => m.hasStoplossClaims);

  // The effective subscriber ID — from stable ref (manage mode) or created subscriber (after "Set Subscriber")
  const effectiveSubscriberRefId = stableSubscriberRefId ?? createdSubscriberId;

  // Filter to eligible claimants for adding: family deductible, no existing family link, not already in this family
  const currentMemberIds = new Set([...(familyData?.members.map((m) => m.id) ?? []), ...localMembers.map((m) => m.id)]);
  if (effectiveSubscriberRefId) currentMemberIds.add(effectiveSubscriberRefId);

  // A claimant is eligible if at least one of their policies has a family deductible endorsement
  const hasAnyFamilyDeductiblePolicy = (c: Claimant) =>
    c.policies?.some((p) => p.has_family_deductible_endorsement) ?? false;

  // Determine the subscriber's family-deductible policy ID to scope "Add Member" to the same policy.
  // The family unit's own policy first (the one its spend is computed on), so a claimant with two
  // family-deductible terms scopes to the unit's term, not whichever is listed first. Then the
  // current claimant's policies (manage mode), then the one stored when the subscriber was set (create mode).
  const subscriberFamilyPolicyId =
    familyData?.subscriber?.policy_id_ref ??
    claimant?.policies?.find((p) => p.has_family_deductible_endorsement)?.id ??
    createdSubscriberPolicyId;

  const isOnSameFamilyPolicy = (c: Claimant) =>
    subscriberFamilyPolicyId ? (c.policies?.some((p) => p.id === subscriberFamilyPolicyId) ?? false) : false;

  // Max members from the family deductible endorsement (null = no limit).
  // Count actual family members (from API + locally added, deduped) — don't use currentMemberIds
  // which also contains the subscriber table UUID and would inflate the count.
  const maxMembers = ownFamilyUnit.data?.maxMembersCounted ?? null;
  const currentMemberCount = calculateFamilyMemberCount(familyData?.members ?? [], localMembers);
  const atCapacity = isAtFamilyCapacity(currentMemberCount, maxMembers);

  const eligibleForAdd = atCapacity
    ? []
    : allClaimants.filter(
        (c) =>
          !c.subscriber_ref_id &&
          !claimantHasStoplossClaims(c) &&
          isOnSameFamilyPolicy(c) &&
          !currentMemberIds.has(c.id?.toString() ?? "")
      );

  // For subscriber picker in create mode: claimants not already in a family, on a family deductible policy, no stoploss
  const eligibleSubscribers = allClaimants.filter(
    (c) => !c.subscriber_ref_id && hasAnyFamilyDeductiblePolicy(c) && !claimantHasStoplossClaims(c)
  );

  const handleMutation = async (action: () => Promise<void>, successMessage: string, onSuccess?: () => void) => {
    setIsSubmitting(true);
    try {
      await action();
      notify(successMessage, { type: "success" });
      refresh();
      ownFamilyUnit.refetch();
      onMutationSuccess();
      onSuccess?.();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResolveSubscriber = () => {
    if (!familyData?.subscriber?.id) return;
    void handleMutation(
      () =>
        httpClient(`${getPartnerAwareApiUrl()}/claimants/subscribers/${familyData.subscriber.id}`, {
          method: "PATCH",
          body: JSON.stringify({ is_resolved: true }),
        }).then(() => undefined),
      "Subscriber resolved"
    );
  };

  const handleConfirm = (memberId: string) => {
    void handleMutation(
      () =>
        update(
          ResourceName.CLAIMANTS,
          { id: memberId, data: { subscriber_ref_id: effectiveSubscriberRefId, is_pending_link: false } },
          { returnPromise: true }
        ),
      "Family link confirmed"
    );
  };

  const handleRemove = (memberId: string) => {
    void handleMutation(
      () =>
        update(ResourceName.CLAIMANTS, { id: memberId, data: { subscriber_ref_id: null } }, { returnPromise: true }),
      "Member removed from family",
      () => setLocalMembers((prev) => prev.filter((m) => m.id !== memberId))
    );
  };

  const handleRelationshipChange = (memberId: string, relationship: ClaimantRelationship) => {
    void handleMutation(
      () =>
        update(
          ResourceName.CLAIMANTS,
          { id: memberId, data: { claimant_relationship: relationship } },
          { returnPromise: true }
        ),
      "Relationship updated"
    );
  };

  const handleSetSubscriber = () => {
    if (!selectedSubscriber) return;

    // Find the first family-deductible policy on this claimant
    const familyPolicy = selectedSubscriber.policies?.find((p) => p.has_family_deductible_endorsement);

    // Note: non-atomic — if the PATCH below fails, the subscriber row created above will be
    // orphaned. A backend /subscribers/create-with-link endpoint would eliminate this.
    void handleMutation(async () => {
      // Step 1: Create a subscriber record
      const { json: newSubscriber } = await httpClient(`${getPartnerAwareApiUrl()}/claimants/subscribers`, {
        method: "POST",
        body: JSON.stringify({
          subscriber_id: selectedSubscriber.member_id || selectedSubscriber.aegle_id,
          subscriber_name: selectedSubscriber.claimant_name,
          client_id_ref: selectedSubscriber.client_id_ref,
          policy_id_ref: familyPolicy?.id ?? "",
          is_resolved: true,
        }),
      });

      // Step 2: Link the claimant to the new subscriber
      const subscriberId = newSubscriber?.id;
      await update(
        ResourceName.CLAIMANTS,
        {
          id: selectedSubscriber.id,
          data: {
            subscriber_ref_id: subscriberId,
            is_pending_link: false,
            claimant_relationship: "self",
          },
        },
        { returnPromise: true }
      );

      // Store the new subscriber ID and policy so the Add Member section appears scoped correctly
      setCreatedSubscriberId(subscriberId);
      setCreatedSubscriberName(selectedSubscriber.claimant_name ?? "Subscriber");
      setCreatedSubscriberPolicyId(familyPolicy?.id ?? null);
      setSelectedSubscriber(null);
    }, `${selectedSubscriber.claimant_name} set as subscriber`);
  };

  const handleAddMember = () => {
    if (!selectedClaimant || !effectiveSubscriberRefId) return;
    const claimantToAdd = selectedClaimant;
    const relationshipToSet = addRelationship;
    void handleMutation(
      () =>
        update(
          ResourceName.CLAIMANTS,
          {
            id: claimantToAdd.id,
            data: {
              subscriber_ref_id: effectiveSubscriberRefId,
              claimant_relationship: relationshipToSet,
            },
          },
          { returnPromise: true }
        ),
      `${claimantToAdd.claimant_name} added to family`
    );
    // Track locally so the member appears in the dialog immediately
    setLocalMembers((prev) => [
      ...prev,
      {
        id: claimantToAdd.id?.toString() ?? "",
        aegleId: claimantToAdd.aegle_id ?? "",
        name: claimantToAdd.claimant_name ?? "Unknown",
        relationship: relationshipToSet,
        isPendingLink: false,
        totalSpend: 0,
      },
    ]);
    setSelectedClaimant(null);
    setAddRelationship("dependent");
  };

  const handleDissolveFamily = () => {
    if (!familyData?.subscriber?.id) return;
    const subscriberId = familyData.subscriber.id;
    const memberIds = familyData.members.map((m) => m.id);

    // Note: non-atomic — two failure modes exist:
    // 1. If some PATCHes fail, members are partially unlinked with the subscriber intact.
    // 2. If all PATCHes succeed but DELETE fails, members are unlinked and the subscriber
    //    row becomes an orphan. A backend /claimants/subscribers/{id}/dissolve endpoint
    //    would eliminate both failure modes.
    void handleMutation(
      async () => {
        // Step 1: Unlink all members (set subscriber_ref_id to null)
        await Promise.all(
          memberIds.map((memberId) =>
            update(ResourceName.CLAIMANTS, { id: memberId, data: { subscriber_ref_id: null } }, { returnPromise: true })
          )
        );

        // Step 2: Delete the subscriber record
        await httpClient(`${getPartnerAwareApiUrl()}/claimants/subscribers/${subscriberId}`, {
          method: "DELETE",
        });
      },
      "Family dissolved",
      () => {
        setShowDissolveConfirm(false);
        handleClose();
      }
    );
  };

  const handleClose = () => {
    setSelectedClaimant(null);
    setSelectedSubscriber(null);
    setCreatedSubscriberId(null);
    setCreatedSubscriberName(null);
    setCreatedSubscriberPolicyId(null);
    setAddRelationship("dependent");
    setShowResolveConfirm(false);
    setShowDissolveConfirm(false);
    setLocalMembers([]);
    onClose();
  };

  // Merge server members with locally-added members, deduplicating by ID
  const serverMemberIds = new Set(familyData?.members.map((m) => m.id) ?? []);
  const allMembers = [...(familyData?.members ?? []), ...localMembers.filter((m) => !serverMemberIds.has(m.id))];

  // Sort members: subscriber first, then alphabetically
  const subscriberMember = allMembers.find((m) => m.relationship === "self");
  const dependentMembers = allMembers
    .filter((m) => m.relationship !== "self")
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle variant="h3" sx={{ borderBottom: 1, borderColor: "divider" }}>
        {isCreateMode ? "Create Family Unit" : "Manage Family Unit"}
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3, "&.MuiDialogContent-root": { pt: 3 } }}>
        <Typography variant="caption" color="text.disabled" sx={{ mb: 2, display: "block" }}>
          Eligible claimants must be on a policy with a family deductible endorsement, not already in a family, and have
          no active stoploss claims.
        </Typography>

        {/* Subscriber unresolved warning + resolve action */}
        {!isCreateMode && familyData && !familyData.subscriber.is_resolved && (
          <Alert
            severity="warning"
            sx={{ mb: 2, "& .MuiAlert-message": { fontSize: "0.8125rem" } }}
            action={
              !showResolveConfirm ? (
                <Button size="small" color="warning" variant="outlined" onClick={() => setShowResolveConfirm(true)}>
                  Resolve
                </Button>
              ) : undefined
            }
          >
            <Typography variant="body2" fontWeight={500} sx={{ mb: showResolveConfirm ? 1 : 0 }}>
              Subscriber unresolved — family calculations are held
            </Typography>
            {showResolveConfirm && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Before resolving, please verify:
                </Typography>
                <Typography
                  variant="caption"
                  component="ul"
                  color="text.secondary"
                  sx={{ pl: 2, mb: 1.5, "& li": { mb: 0.25 } }}
                >
                  <li>The subscriber (employee) name and member ID are correct</li>
                  <li>All family members are linked and accounted for</li>
                  <li>Relationships (spouse, dependent) are accurate</li>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                  Resolving will release held calculations for this family unit.
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    onClick={handleResolveSubscriber}
                    disabled={isSubmitting}
                  >
                    Confirm Resolve
                  </Button>
                  <Button size="small" variant="text" onClick={() => setShowResolveConfirm(false)}>
                    Cancel
                  </Button>
                </Stack>
              </Box>
            )}
          </Alert>
        )}

        {/* Create mode — Subscriber picker or confirmation (shown first, above members) */}
        {isCreateMode && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Subscriber
            </Typography>
            {createdSubscriberId ? (
              <Typography variant="body2" fontWeight={500}>
                {createdSubscriberName}
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Select the subscriber (employee) to anchor this family unit.
                </Typography>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Autocomplete
                    options={eligibleSubscribers}
                    loading={claimantsLoading}
                    getOptionLabel={(c) => c.claimant_name ?? "Unknown"}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selectedSubscriber}
                    onChange={(_e, newValue) => setSelectedSubscriber(newValue)}
                    renderOption={(props, option) => {
                      const { key, ...rest } = props;
                      return (
                        <Box component="li" key={key} {...rest}>
                          <Typography variant="body2">{option.claimant_name}</Typography>
                        </Box>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Subscriber" placeholder="Search claimants..." size="small" />
                    )}
                    noOptionsText="No eligible claimants found"
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSetSubscriber}
                    disabled={!selectedSubscriber || isSubmitting}
                    sx={{ height: 40 }}
                  >
                    Set Subscriber
                  </Button>
                </Stack>
              </>
            )}
          </Box>
        )}

        {/* Stoploss claims warning — locks add/remove */}
        {hasStoplossClaims && (
          <Alert severity="warning" sx={{ mb: 2, "& .MuiAlert-message": { fontSize: "0.8125rem" } }}>
            This family has active stoploss claims. Adding or removing members is disabled to preserve claim integrity.
            To modify the family, dissolve or close the active stoploss claims first.
          </Alert>
        )}

        {/* Current Members — from server data and locally-added */}
        {allMembers.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Family Members
            </Typography>
            <Stack spacing={0.5}>
              {subscriberMember && (
                <MemberManageRow
                  member={subscriberMember}
                  isSubscriber
                  onConfirm={handleConfirm}
                  onRemove={handleRemove}
                  onRelationshipChange={handleRelationshipChange}
                  disableRemove={hasStoplossClaims}
                />
              )}
              {subscriberMember && dependentMembers.length > 0 && <Divider sx={{ mx: 1.5 }} />}
              {dependentMembers.map((member) => (
                <MemberManageRow
                  key={member.id}
                  member={member}
                  isSubscriber={false}
                  onConfirm={handleConfirm}
                  onRemove={handleRemove}
                  disableRemove={hasStoplossClaims}
                  onRelationshipChange={handleRelationshipChange}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Add Member — shown when a subscriber exists and no stoploss claims */}
        {effectiveSubscriberRefId && !hasStoplossClaims && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              <PersonAddIcon sx={{ fontSize: 16, verticalAlign: "text-bottom", mr: 0.5 }} />
              Add Member
              {maxMembers !== null && (
                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                  ({currentMemberCount}/{maxMembers})
                </Typography>
              )}
            </Typography>
            {atCapacity ? (
              <Alert severity="info" sx={{ mb: 1 }}>
                This family has reached the maximum of {maxMembers} members allowed by the endorsement.
              </Alert>
            ) : (
              <Typography variant="caption" color="text.disabled" sx={{ mb: 1, display: "block" }}>
                Showing claimants on the same policy, not already in a family, and without active stoploss claims.
              </Typography>
            )}
            {!atCapacity && (
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Autocomplete
                  options={eligibleForAdd}
                  loading={claimantsLoading}
                  getOptionLabel={(c) => c.claimant_name ?? "Unknown"}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={selectedClaimant}
                  onChange={(_e, newValue) => setSelectedClaimant(newValue)}
                  renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                      <Box component="li" key={key} {...rest}>
                        <Typography variant="body2">{option.claimant_name}</Typography>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Claimant" placeholder="Search claimants..." size="small" />
                  )}
                  noOptionsText="No eligible claimants found"
                  sx={{ flex: 1 }}
                />
                <Select
                  size="small"
                  value={addRelationship}
                  onChange={(e) => setAddRelationship(e.target.value as ClaimantRelationship)}
                  sx={{ minWidth: 120, height: 40 }}
                >
                  {CLAIMANT_RELATIONSHIP_CHOICES.filter((c) => c.id !== "self").map((choice) => (
                    <MenuItem key={choice.id} value={choice.id}>
                      {choice.name}
                    </MenuItem>
                  ))}
                </Select>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddMember}
                  disabled={!selectedClaimant || isSubmitting}
                  sx={{ height: 40, minWidth: 70 }}
                >
                  Add
                </Button>
              </Stack>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: 1, borderColor: "divider", px: 3, py: 2, justifyContent: "space-between" }}>
        {!isCreateMode && familyData ? (
          <Box>
            {showDissolveConfirm ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="error">
                  This will unlink all members and remove the family unit. No claimant records will be deleted.
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="contained"
                  onClick={handleDissolveFamily}
                  disabled={isSubmitting}
                >
                  Confirm
                </Button>
                <Button size="small" onClick={() => setShowDissolveConfirm(false)}>
                  Cancel
                </Button>
              </Stack>
            ) : (
              <Button
                size="small"
                color="error"
                startIcon={<GroupRemoveIcon />}
                onClick={() => setShowDissolveConfirm(true)}
                disabled={isSubmitting || hasStoplossClaims}
              >
                Dissolve Family
              </Button>
            )}
          </Box>
        ) : (
          <Box />
        )}
        <Button variant="outlined" onClick={handleClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
